// src/components/tuition/HostWaitingRoomControls.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaCheck,
  FaTimes,
  FaUserClock,
  FaShieldAlt,
  FaCheckDouble,
  FaChevronDown,
  FaChevronUp,
  FaGripVertical,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  admitAllStudents,
  admitStudent,
  denyStudent,
  getRoomSettings,
  saveRoomSettings,
  subscribeToWaitingRoomRequests,
  updateRoomMediaSettings,
} from "../../services/classroomWaitingRoomService";

function KnockAdmissionPopup({ request, roomCode, onAdmit, onDeny, onLater }) {
  if (!request) return null;
  return createPortal(
    <div className="fixed inset-0 z-[250] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Student admission request">
      <div className="w-full max-w-sm rounded-3xl border border-sky-500/40 bg-slate-900 p-6 text-center text-white shadow-2xl shadow-sky-950/50">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sky-500/15 text-3xl text-sky-400 ring-1 ring-sky-500/30"><FaUserClock /></div>
        <p className="mt-4 text-xs font-black uppercase tracking-[0.2em] text-sky-400">Waiting room</p>
        <h2 className="mt-2 text-xl font-black">{request.studentName} wants to join</h2>
        <p className="mt-2 text-sm text-slate-400">
          {request.rollOrPhone || "Guest Student"} entered meeting code <strong className="text-slate-200">{roomCode}</strong>.
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button type="button" onClick={() => onDeny(request.id, request.studentName)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-sm font-bold text-slate-200 hover:bg-rose-950/60 hover:text-rose-300"><FaTimes /> Decline</button>
          <button type="button" onClick={() => onAdmit(request.id, request.studentName)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-lg hover:bg-emerald-500"><FaCheck /> Admit</button>
        </div>
        <button type="button" onClick={onLater} className="mt-4 text-xs font-semibold text-slate-500 hover:text-slate-300">Review later in waiting room</button>
      </div>
    </div>,
    document.body
  );
}

export default function HostWaitingRoomControls({
  roomCode,
  isHost = true,
  embedded = false,
  onPendingCountChange,
}) {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [settings, setSettings] = useState(() => getRoomSettings(roomCode));
  const [isExpanded, setIsExpanded] = useState(embedded ? true : false);
  const [showSettings, setShowSettings] = useState(false);
  const [savingMediaSettings, setSavingMediaSettings] = useState(false);
  const [panelPosition, setPanelPosition] = useState(null);
  const [knockPopup, setKnockPopup] = useState(null);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const lastNotifiedRequestIdRef = useRef(null);

  useEffect(() => {
    if (!roomCode || !isHost) return;

    const unsubscribe = subscribeToWaitingRoomRequests({
      roomCode,
      onUpdate: ({ pending, all }) => {
        // If new pending request arrived, notify host
        setPendingRequests((prev) => {
          const previousIds = new Set(prev.map((request) => request.id));
          const newest = [...pending]
            .reverse()
            .find((request) => !previousIds.has(request.id)) || pending[pending.length - 1];
          if (newest && newest.id !== lastNotifiedRequestIdRef.current) {
            lastNotifiedRequestIdRef.current = newest.id;
            setIsExpanded(true);
            setKnockPopup(newest);
            toast.info(`🔔 ${newest.studentName} is knocking to enter the classroom.`, {
              toastId: `knock-${newest.id}`,
            });
          }
          onPendingCountChange?.(pending.length);
          return pending;
        });
        setAllRequests(all);
      },
    });

    setSettings(getRoomSettings(roomCode));

    return () => {
      unsubscribe();
    };
  }, [roomCode, isHost, onPendingCountChange]);

  useEffect(() => {
    if (!panelPosition) return undefined;
    const keepPanelOnScreen = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const margin = 8;
      setPanelPosition((current) => current && ({
        left: Math.max(margin, Math.min(current.left, window.innerWidth - panel.offsetWidth - margin)),
        top: Math.max(margin, Math.min(current.top, window.innerHeight - panel.offsetHeight - margin)),
      }));
    };
    window.addEventListener("resize", keepPanelOnScreen);
    return () => window.removeEventListener("resize", keepPanelOnScreen);
  }, [panelPosition]);

  const handleDragStart = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (event.target.closest("button")) return;
    const panel = panelRef.current;
    if (!panel) return;
    const bounds = panel.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: bounds.left,
      startTop: bounds.top,
      width: bounds.width,
      height: bounds.height,
    };
    setPanelPosition({ left: bounds.left, top: bounds.top });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleDragMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const margin = 8;
    setPanelPosition({
      left: Math.max(margin, Math.min(drag.startLeft + event.clientX - drag.startX, window.innerWidth - drag.width - margin)),
      top: Math.max(margin, Math.min(drag.startTop + event.clientY - drag.startY, window.innerHeight - drag.height - margin)),
    });
  };

  const handleDragEnd = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  if (!isHost) return null;

  const handleAdmit = async (requestId, name) => {
    try {
      await admitStudent(roomCode, requestId);
      setKnockPopup((current) => current?.id === requestId ? null : current);
      toast.success(`Admitted ${name} into the class!`);
    }
    catch (error) { toast.error(error?.message || `Unable to admit ${name}.`); }
  };

  const handleDeny = async (requestId, name) => {
    try {
      await denyStudent(roomCode, requestId, "The host declined your entry request.");
      setKnockPopup((current) => current?.id === requestId ? null : current);
      toast.warn(`Declined entry for ${name}.`);
    }
    catch (error) { toast.error(error?.message || `Unable to decline ${name}.`); }
  };

  const handleAdmitAll = async () => {
    if (pendingRequests.length === 0) return;
    try { await admitAllStudents(roomCode); toast.success(`Admitted all ${pendingRequests.length} waiting students!`); }
    catch (error) { toast.error(error?.message || "Unable to admit waiting students."); }
  };

  const toggleAdmissionRequirement = () => {
    const nextVal = !settings.requireAdmission;
    const updated = saveRoomSettings(roomCode, { requireAdmission: nextVal });
    setSettings(updated);
    toast.info(
      nextVal
        ? "Waiting Room Enabled: Host admission is required for students to enter."
        : "Waiting Room Disabled: Students can now join directly without waiting."
    );
  };

  const toggleMediaPermission = async (setting) => {
    if (savingMediaSettings) return;
    const previous = settings;
    const next = { ...settings, [setting]: !settings[setting] };
    setSettings(next);
    setSavingMediaSettings(true);
    try {
      const updated = await updateRoomMediaSettings(roomCode, next);
      setSettings(updated);
      toast.success("Student media permissions updated. Students must reconnect for the change to apply.");
    } catch (error) {
      setSettings(previous);
      toast.error(error?.message || "Unable to update student media permissions.");
    } finally {
      setSavingMediaSettings(false);
    }
  };

  if (embedded) {
    return (
      <div className="flex h-full w-full flex-col space-y-3">
        <KnockAdmissionPopup request={knockPopup} roomCode={roomCode} onAdmit={handleAdmit} onDeny={handleDeny} onLater={() => setKnockPopup(null)} />
        {/* Waiting Room Top Bar */}
        <div className="flex items-center justify-between rounded-xl bg-slate-950/80 px-3 py-2 border border-slate-800">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              {pendingRequests.length > 0 && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              )}
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${pendingRequests.length > 0 ? "bg-emerald-500" : "bg-slate-500"}`} />
            </span>
            <div className="flex items-center gap-1.5">
              <FaUserClock className="text-sky-400 text-sm" />
              <span className="text-xs font-bold text-white tracking-tight">Knock Requests</span>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                pendingRequests.length > 0
                  ? "bg-emerald-500 text-slate-950 animate-pulse"
                  : "bg-slate-800 text-slate-400"
              }`}
            >
              {pendingRequests.length} waiting
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            {pendingRequests.length > 1 && (
              <button
                type="button"
                onClick={handleAdmitAll}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white shadow hover:bg-emerald-500 transition"
              >
                <FaCheckDouble /> Admit All
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowSettings(!showSettings)}
              className={`rounded-lg p-1.5 text-xs transition ${showSettings ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
              title="Classroom Entrance & Media Settings"
              aria-label="Classroom Entrance & Media Settings"
            >
              <FaShieldAlt />
            </button>
          </div>
        </div>

        {/* Quick Settings Drawer if toggled */}
        {showSettings && (
          <div className="rounded-xl border border-indigo-500/30 bg-slate-950/90 p-3 text-xs space-y-2.5 shadow-lg">
            <div className="flex items-center justify-between">
              <span className="text-slate-300 font-semibold">Require Host Admission:</span>
              <button
                type="button"
                onClick={toggleAdmissionRequirement}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  settings.requireAdmission ? "bg-emerald-600" : "bg-slate-700"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    settings.requireAdmission ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <p className="text-[11px] text-slate-400">
              {settings.requireAdmission
                ? "Active: Students must knock and be admitted by you before entering."
                : "Inactive: Students enter the classroom directly without waiting."}
            </p>

            <div className="space-y-2 border-t border-slate-800 pt-2">
              <p className="font-semibold text-slate-300">Default Student Media</p>
              {[
                ["allowStudentAudio", "Allow student microphones"],
                ["allowStudentVideo", "Allow student cameras"],
              ].map(([setting, label]) => (
                <div key={setting} className="flex items-center justify-between gap-3">
                  <span className="text-slate-300 text-[11px]">{label}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={Boolean(settings[setting])}
                    aria-label={label}
                    disabled={savingMediaSettings}
                    onClick={() => toggleMediaPermission(setting)}
                    className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors disabled:cursor-wait disabled:opacity-60 ${settings[setting] ? "bg-emerald-600" : "bg-slate-700"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${settings[setting] ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>
              ))}
              <p className="text-[10px] text-amber-300/80">
                Note: Students already in class must reconnect for permission changes to apply.
              </p>
            </div>
          </div>
        )}

        {/* Pending Requests List */}
        <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[120px]">
          {pendingRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-slate-500">
              <div className="h-10 w-10 rounded-2xl bg-slate-800/60 flex items-center justify-center text-slate-400 mb-2">
                <FaUserClock className="text-xl" />
              </div>
              <p className="text-xs font-semibold text-slate-400">Waiting Room is Empty</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Students who knock will appear here instantly for admission.</p>
            </div>
          ) : (
            pendingRequests.map((req) => (
              <div
                key={req.id}
                className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/80 p-2.5 text-xs shadow-sm transition hover:border-slate-700"
              >
                <div className="min-w-0 flex-1 pr-2">
                  <p className="font-bold text-white truncate text-xs">{req.studentName}</p>
                  <p className="text-[10px] text-slate-400 truncate">
                    {req.rollOrPhone || "Student"} &bull; {new Date(req.requestedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleAdmit(req.id, req.studentName)}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white shadow hover:bg-emerald-500 transition"
                    title="Admit Student"
                  >
                    <FaCheck className="text-[10px]" /> Admit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeny(req.id, req.studentName)}
                    className="rounded-lg bg-slate-800 p-1.5 text-slate-400 hover:bg-rose-950/50 hover:text-rose-400 transition"
                    title="Decline Entry"
                  >
                    <FaTimes className="text-[10px]" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] max-w-[20rem]"
      style={panelPosition ? { left: panelPosition.left, top: panelPosition.top, right: "auto", bottom: "auto" } : undefined}
    >
      <KnockAdmissionPopup request={knockPopup} roomCode={roomCode} onAdmit={handleAdmit} onDeny={handleDeny} onLater={() => setKnockPopup(null)} />
      <div className="overflow-hidden rounded-xl border border-indigo-500/30 bg-slate-900/95 text-white shadow-xl backdrop-blur-md transition-all">
        {/* Header Bar */}
        <div
          className={`flex cursor-grab touch-none select-none items-center justify-between bg-gradient-to-r from-slate-900 to-indigo-950/60 px-2.5 py-2 active:cursor-grabbing ${isExpanded ? "border-b border-slate-800" : ""}`}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
          title="Drag to move the waiting room"
        >
          <div className="flex items-center gap-2">
            <FaGripVertical className="shrink-0 text-[10px] text-slate-600" aria-hidden="true" />
            <span className="flex h-2.5 w-2.5 relative">
              {pendingRequests.length > 0 && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              )}
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${pendingRequests.length > 0 ? "bg-emerald-500" : "bg-slate-500"}`} />
            </span>
            <div className="flex items-center gap-1.5">
              <FaUserClock className="text-sky-400 text-sm" />
              <span className="text-[11px] font-bold tracking-tight">Waiting Room</span>
              <span
                className={`rounded-full px-1.5 py-0.5 text-[9px] font-black ${
                  pendingRequests.length > 0
                    ? "bg-emerald-500 text-slate-950 animate-pulse"
                    : "bg-slate-800 text-slate-400"
                }`}
              >
                {pendingRequests.length} waiting
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowSettings(!showSettings)}
              className={`rounded-lg p-1.5 text-xs transition ${showSettings ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
              title="Waiting Room Settings"
              aria-label="Waiting room and student media settings"
              aria-expanded={showSettings}
            >
              <FaShieldAlt />
            </button>
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="rounded-lg p-1.5 text-xs text-slate-400 hover:text-white transition"
              title={isExpanded ? "Collapse" : "Expand"}
              aria-label={isExpanded ? "Collapse waiting room" : "Expand waiting room"}
              aria-expanded={isExpanded}
            >
              {isExpanded ? <FaChevronDown /> : <FaChevronUp />}
            </button>
          </div>
        </div>

        {/* Collapsible Content */}
        {isExpanded && (
          <div className="space-y-2 p-2.5">
            {/* Quick Settings Panel if toggled */}
            {showSettings && (
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-2.5 text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 font-semibold">Require Host Admission:</span>
                  <button
                    type="button"
                    onClick={toggleAdmissionRequirement}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      settings.requireAdmission ? "bg-emerald-600" : "bg-slate-700"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        settings.requireAdmission ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
                <p className="text-[11px] text-slate-400">
                  {settings.requireAdmission
                    ? "Active: Only students you approve can enter this meeting."
                    : "Inactive: Students with the meeting code will enter immediately."}
                </p>
                <div className="space-y-2 border-t border-slate-800 pt-2">
                  <p className="font-semibold text-slate-300">Student media</p>
                  {[
                    ["allowStudentAudio", "Allow microphones"],
                    ["allowStudentVideo", "Allow cameras"],
                  ].map(([setting, label]) => (
                    <div key={setting} className="flex items-center justify-between gap-3">
                      <span className="text-slate-300">{label}</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={Boolean(settings[setting])}
                        aria-label={label}
                        disabled={savingMediaSettings}
                        onClick={() => toggleMediaPermission(setting)}
                        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors disabled:cursor-wait disabled:opacity-60 ${settings[setting] ? "bg-emerald-600" : "bg-slate-700"}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${settings[setting] ? "translate-x-4" : "translate-x-0"}`} />
                      </button>
                    </div>
                  ))}
                  <p className="text-[10px] leading-relaxed text-amber-300/80">
                    Students already in class must reconnect after a permission change.
                  </p>
                </div>
              </div>
            )}

            {/* Pending Requests List */}
            {pendingRequests.length === 0 ? (
              <div className="py-2 text-center text-[11px] text-slate-400">
                <p>No one is waiting.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {pendingRequests.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/80 p-2 text-xs shadow-sm transition hover:border-slate-700"
                  >
                    <div className="min-w-0 flex-1 pr-2">
                      <p className="font-bold text-white truncate">{req.studentName}</p>
                      <p className="text-[10px] text-slate-400 truncate">
                        {req.rollOrPhone || "Guest Student"} • {new Date(req.requestedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleAdmit(req.id, req.studentName)}
                        className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white shadow hover:bg-emerald-500 transition"
                        title="Admit Student"
                      >
                        <FaCheck className="text-[10px]" /> Admit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeny(req.id, req.studentName)}
                        className="rounded-lg bg-slate-800 p-1.5 text-slate-400 hover:bg-rose-950/50 hover:text-rose-400 transition"
                        title="Decline Entry"
                      >
                        <FaTimes className="text-[10px]" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer with Admit All */}
            {pendingRequests.length > 1 && (
              <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
                <span className="text-[11px] text-slate-400">{pendingRequests.length} waiting</span>
                <button
                  type="button"
                  onClick={handleAdmitAll}
                  className="inline-flex items-center gap-1 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-3 py-1.5 text-xs font-bold text-white shadow-md hover:opacity-95 transition"
                >
                  <FaCheckDouble /> Admit All
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
